import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, fetch} from '../services/ReportRunsService';
import {ReportRun} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

/** Stripe epoch SECONDS to milliseconds; anything else (null, undefined) to undefined. */
const toMillis = (seconds?: number | null) =>
    typeof seconds === 'number' ? seconds * 1000 : undefined;

/**
 * FNP-1106: the detail view's half of the FNP-918 fix. Stripe hands back epoch
 * SECONDS; the shared timestamp formatter reads a bare number as MILLISECONDS.
 * Normalise ONCE, here, so the profile needs no `as unknown as string` cast —
 * the cast is what hid the wrong unit from the compiler.
 */
const normalise = (record?: ReportRun): ReportRun | undefined => record && {
    ...record,
    createdAtMillis: toMillis(record.created),
    succeededAtMillis: toMillis(record.succeededAt),
    intervalStartMillis: toMillis(record.parameters?.intervalStart),
    intervalEndMillis: toMillis(record.parameters?.intervalEnd),
};

export const useReportRun = (reportRunId?: string) => {
    const queryClient = useQueryClient();

    const reportRunQuery = useQuery({
        queryKey: ['reportRun', reportRunId],
        queryFn: () => fetch(reportRunId!),
        enabled: !!reportRunId,
        select: normalise,
    });

    const createMutation = useMutation({
        mutationFn: (values: ReportRun) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.id;
            delete payload.created;
            delete payload.succeededAt;
            return add(payload as ReportRun);
        },
        onSuccess: () => {
            notifySuccess('Report run created');
            queryClient.invalidateQueries({queryKey: ['reportRuns']});
        },
        onError: notifyError,
    });

    return {
        record: reportRunQuery.data,
        loading: reportRunQuery.isLoading,
        fetchRecord: reportRunQuery.refetch,
        createRecord: createMutation.mutate,
        createMutation,
    };
};
